// src/store/reducers/entityReducer.js
import { ENTITY_ACTIONS } from '../actions/actionTypes';

const initialState = {
  entities: [],
  selectedEntity: null,
  loading: false,
  error: null,
  success: null,
  meta: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  },
  filters: {
    page: 1,
    limit: 20,
    search: '',
    isOpen: undefined,
    minRating: undefined,
    city: '',
    sortBy: 'name',
    sortOrder: 'asc',
  },
  products: [],
  productsMeta: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  },
  agencies: [],
  agenciesMeta: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  },
  uploadProgress: 0,
};

const entityReducer = (state = initialState, action) => {
  switch (action.type) {
    // Fetch entities
    case ENTITY_ACTIONS.FETCH_ENTITIES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ENTITY_ACTIONS.FETCH_ENTITIES_SUCCESS:
      return {
        ...state,
        loading: false,
        entities: action.payload.data || [],
        meta: action.payload.meta || initialState.meta,
        error: null,
      };

    case ENTITY_ACTIONS.FETCH_ENTITIES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        entities: [],
      };

    // Fetch single entity
    case ENTITY_ACTIONS.FETCH_ENTITY_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ENTITY_ACTIONS.FETCH_ENTITY_SUCCESS:
      return {
        ...state,
        loading: false,
        selectedEntity: action.payload.data || null,
        error: null,
      };

    case ENTITY_ACTIONS.FETCH_ENTITY_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        selectedEntity: null,
      };

    // Create entity
    case ENTITY_ACTIONS.CREATE_ENTITY_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };

    case ENTITY_ACTIONS.CREATE_ENTITY_SUCCESS:
      return {
        ...state,
        loading: false,
        entities: [action.payload, ...state.entities],
        success: 'Entity created successfully',
        error: null,
      };

    case ENTITY_ACTIONS.CREATE_ENTITY_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null,
      };

    // Update entity
    case ENTITY_ACTIONS.UPDATE_ENTITY_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };

    case ENTITY_ACTIONS.UPDATE_ENTITY_SUCCESS:
      return {
        ...state,
        loading: false,
        entities: state.entities.map(entity =>
          entity.id === action.payload.id ? action.payload : entity
        ),
        selectedEntity: action.payload,
        success: 'Entity updated successfully',
        error: null,
      };

    case ENTITY_ACTIONS.UPDATE_ENTITY_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null,
      };

    // Delete entity
    case ENTITY_ACTIONS.DELETE_ENTITY_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        success: null,
      };

    case ENTITY_ACTIONS.DELETE_ENTITY_SUCCESS:
      return {
        ...state,
        loading: false,
        entities: state.entities.filter(entity => entity.id !== action.payload.id),
        success: action.payload.message || 'Entity deleted successfully',
        error: null,
      };

    case ENTITY_ACTIONS.DELETE_ENTITY_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        success: null,
      };

    // Toggle entity status
    case ENTITY_ACTIONS.TOGGLE_ENTITY_STATUS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ENTITY_ACTIONS.TOGGLE_ENTITY_STATUS_SUCCESS:
      return {
        ...state,
        loading: false,
        entities: state.entities.map(entity =>
          entity.id === action.payload.id 
            ? { ...entity, isOpen: action.payload.isOpen }
            : entity
        ),
        selectedEntity: state.selectedEntity?.id === action.payload.id
          ? { ...state.selectedEntity, isOpen: action.payload.isOpen }
          : state.selectedEntity,
        success: `Entity ${action.payload.isOpen ? 'opened' : 'closed'} successfully`,
        error: null,
      };

    case ENTITY_ACTIONS.TOGGLE_ENTITY_STATUS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Upload entity image
    case ENTITY_ACTIONS.UPLOAD_ENTITY_IMAGE_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        uploadProgress: 0,
      };

    case ENTITY_ACTIONS.UPLOAD_ENTITY_IMAGE_SUCCESS:
      return {
        ...state,
        loading: false,
        selectedEntity: {
          ...state.selectedEntity,
          image: action.payload.image,
        },
        uploadProgress: 100,
        success: 'Image uploaded successfully',
        error: null,
      };

    case ENTITY_ACTIONS.UPLOAD_ENTITY_IMAGE_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        uploadProgress: 0,
      };

    // Fetch entity products
    case ENTITY_ACTIONS.FETCH_ENTITY_PRODUCTS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ENTITY_ACTIONS.FETCH_ENTITY_PRODUCTS_SUCCESS:
      return {
        ...state,
        loading: false,
        products: action.payload.data || [],
        productsMeta: action.payload.meta || initialState.productsMeta,
        error: null,
      };

    case ENTITY_ACTIONS.FETCH_ENTITY_PRODUCTS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        products: [],
      };

    // Fetch entity agencies
    case ENTITY_ACTIONS.FETCH_ENTITY_AGENCIES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ENTITY_ACTIONS.FETCH_ENTITY_AGENCIES_SUCCESS:
      return {
        ...state,
        loading: false,
        agencies: action.payload.data || [],
        agenciesMeta: action.payload.meta || initialState.agenciesMeta,
        error: null,
      };

    case ENTITY_ACTIONS.FETCH_ENTITY_AGENCIES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
        agencies: [],
      };

    // Bulk delete
    case ENTITY_ACTIONS.BULK_DELETE_ENTITIES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ENTITY_ACTIONS.BULK_DELETE_ENTITIES_SUCCESS:
      return {
        ...state,
        loading: false,
        entities: state.entities.filter(entity => !action.payload.ids.includes(entity.id)),
        success: action.payload.message || 'Entities deleted successfully',
        error: null,
      };

    case ENTITY_ACTIONS.BULK_DELETE_ENTITIES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Set filters
    case ENTITY_ACTIONS.SET_ENTITY_FILTERS:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...action.payload,
        },
      };

    // Clear states
    case ENTITY_ACTIONS.CLEAR_ENTITY_ERROR:
      return {
        ...state,
        error: null,
      };

    case ENTITY_ACTIONS.CLEAR_ENTITY_SUCCESS:
      return {
        ...state,
        success: null,
      };

    case ENTITY_ACTIONS.CLEAR_SELECTED_ENTITY:
      return {
        ...state,
        selectedEntity: null,
      };

    default:
      return state;
  }
};

export default entityReducer;